import { useState, useEffect } from "react";
import AttendancePageSwapper from "../../../components/Attendance/AttendancePageSwapper";
import AttendanceLessonsList, { Lesson } from "../../../components/Attendance/AttendanceLessonsList";
import AttendanceCourseDetails from "../../../components/Attendance/AttendanceCourseDetails";
import CreateLessonModal, { LessonFormData } from "../../../components/Attendance/CreateLessonModal";
import AttendanceWeeklySchedule from "../../../components/Attendance/AttendanceWeeklySchedule";
import AddCourseModal from "../../../components/Attendance/AddCourseModal";
 
// Should come from API
// Структура: для каждого курса отдельный массив уроков
const coursesData: Record<string, Lesson[]> = {
  'Introduction to Management': [
    { id: 11, date: "10.08.2024", averageScore: 4.9 },
    { id: 12, date: "09.08.2024", averageScore: 4.8 },
    { id: 13, date: "08.08.2024", averageScore: 4.7 },
    { id: 14, date: "07.08.2024", averageScore: 4.6 },
  ],
  'ICT': [
    { id: 21, date: "15.08.2024", averageScore: 4.5 },
    { id: 22, date: "14.08.2024", averageScore: 4.7 },
    { id: 23, date: "13.08.2024", averageScore: 4.8 },
    { id: 24, date: "12.08.2024", averageScore: 4.6 },
    { id: 25, date: "11.08.2024", averageScore: 4.9 },
  ],
}

const courses = ['Расписание всех предметов на неделю', ...Object.keys(coursesData)];

// Хранилище описаний уроков (lessonId -> description)
// В реальном приложении это будет приходить с бэкэнда
const lessonDescriptions: Record<number, string> = {
  1: "Обсудим книгу \"Тысячеликий герой\" и различные варианты становления персонажа в рамках традиционного эпоса",
  3: "Введение в основы программирования и алгоритмического мышления",
  // Добавьте описания для других уроков по необходимости
};

export default function AttendancePage() {
  const [selectedPage, setSelectedPage] = useState<string>(courses[0]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | undefined>(undefined);
  const [isCreateLessonModalOpen, setIsCreateLessonModalOpen] = useState(false);
  const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
  const [descriptions, setDescriptions] = useState<Record<number, string>>(lessonDescriptions);
  const [lessons, setLessons] = useState<Record<string, Lesson[]>>(coursesData);
  const [courseList, setCourseList] = useState<string[]>(courses);
  
  // Получаем уроки для выбранного курса
  const currentLessons = lessons[selectedPage] || [];
  
  // Проверяем, выбрана ли вкладка "Расписание всех предметов на неделю"
  const isWeeklyScheduleView = selectedPage === 'Расписание всех предметов на неделю';

  // Сброс выбранного урока при смене курса
  useEffect(() => {
    setSelectedLesson(undefined);
  }, [selectedPage]);


  const handleCreateLesson = () => {
    setIsCreateLessonModalOpen(true);
  };

  const handleSubmitLesson = (lessonData: LessonFormData) => {
    // TODO: Отправить данные на бэкэнд когда будет готов API
    console.log("Creating lesson with data:", lessonData);
    
    // Симуляция создания урока
    // Генерируем новый ID для урока
    const newLessonId = Math.max(...Object.values(lessons).flat().map(l => l.id), 0) + 1;
    
    // Форматируем дату
    const formattedDate = lessonData.date 
      ? lessonData.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : new Date().toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    // Создаем новый урок
    const newLesson: Lesson = {
      id: newLessonId,
      date: formattedDate,
      averageScore: 0, // По умолчанию
    };
    
    // Добавляем урок в текущий курс
    setLessons(prev => ({
      ...prev,
      [selectedPage]: [...(prev[selectedPage] || []), newLesson]
    }));
    
    // Сохраняем описание если оно есть
    if (lessonData.description) {
      setDescriptions(prev => ({
        ...prev,
        [newLessonId]: lessonData.description
      }));
    }
    
    // В реальном приложении здесь будет вызов API:
    // await lessonsApi.create({
    //   course: selectedPage,
    //   cabinet: lessonData.cabinet,
    //   subject_name: lessonData.subjectName,
    //   description: lessonData.description,
    //   lesson_type: lessonData.lessonType,
    //   date: lessonData.date,
    //   start_time: lessonData.startTime,
    //   end_time: lessonData.endTime,
    // });
  };

  const handleDownloadReport = (lessonId: number) => {
    // TODO: Implement download report functionality
    console.log("Download report for lesson", lessonId);
  };

  const handleEditLesson = (lessonId: number) => {
    // TODO: Implement edit lesson functionality
    console.log("Edit lesson", lessonId);
  };

  const handleDeleteLesson = (lessonId: number) => {
    // TODO: Implement delete lesson functionality
    console.log("Delete lesson", lessonId);
  };

  const handleEditDescription = (lessonId: number, newDescription: string) => {
    // TODO: Отправить описание на бэкэнд когда будет готов API
    console.log("Updating description for lesson", lessonId, ":", newDescription);
    
    // Сохраняем описание в состояние
    setDescriptions(prev => ({
      ...prev,
      [lessonId]: newDescription
    }));
    
    // В реальном приложении здесь будет вызов API:
    // await lessonsApi.updateDescription(lessonId, newDescription);
  };

  const handleUploadTest = () => {
    // TODO: Implement upload test functionality
    console.log("Upload test");
  };

  const handleUploadHomework = () => {
    // TODO: Implement upload homework functionality
    console.log("Upload homework");
  };

  const handleEditTest = () => {
    // TODO: Implement edit test functionality
    console.log("Edit test");
  };

  const handleDeleteTest = () => {
    // TODO: Implement delete test functionality
    console.log("Delete test");
  };

  const handleEditHomework = () => {
    // TODO: Implement edit homework functionality
    console.log("Edit homework");
  };

  const handleDeleteHomework = () => {
    // TODO: Implement delete homework functionality
    console.log("Delete homework");
  };

  const handleUploadAdditionalMaterial = () => {
    // TODO: Implement upload additional material functionality
    console.log("Upload additional material");
  };

  const handleEditAdditionalMaterial = () => {
    // TODO: Implement edit additional material functionality
    console.log("Edit additional material");
  };

  const handleDeleteAdditionalMaterial = () => {
    // TODO: Implement delete additional material functionality
    console.log("Delete additional material");
  };

  const handleEditAttendance = () => {
    // TODO: Implement edit attendance functionality
    console.log("Edit attendance");
  };

  const handleAddCourse = (courseName: string) => {
    // TODO: Отправить данные на бэкэнд когда будет готов API
    console.log("Adding course:", courseName);
    
    // Добавляем курс в список (после "Расписание всех предметов на неделю")
    setCourseList(prev => {
      const weeklySchedule = 'Расписание всех предметов на неделю';
      const otherCourses = prev.filter(c => c !== weeklySchedule);
      return [weeklySchedule, ...otherCourses, courseName];
    });
    
    // Инициализируем пустой массив уроков для нового курса
    setLessons(prev => ({
      ...prev,
      [courseName]: []
    }));
    
    // Переключаемся на новый курс
    setSelectedPage(courseName);
    
    // В реальном приложении здесь будет вызов API:
    // await coursesApi.create({ name: courseName });
  };

  return (
    <div className="bg-[#f8f9fd] h-full text-black p-4 md:p-5 text-sm overflow-y-auto">
      <div className="mb-4">
        <AttendancePageSwapper 
          pages={courseList} 
          selectedPage={selectedPage} 
          setSelectedPage={setSelectedPage}
          onAddCourse={() => setIsAddCourseModalOpen(true)}
        />
      </div>
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-160px)] md:h-[calc(100vh-180px)] min-h-0">
        {isWeeklyScheduleView ? (
          // Показываем недельное расписание для вкладки "Расписание всех предметов на неделю"
          <div className="w-full">
            <AttendanceWeeklySchedule lessons={[]} />
          </div>
        ) : (
          // Показываем список уроков для конкретных курсов
          selectedLesson ? (
            <>
              <div className="w-full lg:w-2/5 lg:min-w-[400px] flex-shrink-0 flex flex-col min-h-0">
                <AttendanceLessonsList
                  lessons={currentLessons}
                  selectedLesson={selectedLesson}
                  onLessonSelect={setSelectedLesson}
                  onCreateLesson={handleCreateLesson}
                />
              </div>
              <div className="w-full lg:flex-1 min-w-0 flex flex-col min-h-0">
                <AttendanceCourseDetails
                  selectedLessonId={selectedLesson.id}
                  description={descriptions[selectedLesson.id] || ''}
                  onEditDescription={(newDescription) => handleEditDescription(selectedLesson.id, newDescription)}
                  onUploadTest={handleUploadTest}
                  onEditTest={handleEditTest}
                  onDeleteTest={handleDeleteTest}
                  onUploadHomework={handleUploadHomework}
                  onEditHomework={handleEditHomework}
                  onDeleteHomework={handleDeleteHomework}
                  onUploadAdditionalMaterial={handleUploadAdditionalMaterial}
                  onEditAdditionalMaterial={handleEditAdditionalMaterial}
                  onDeleteAdditionalMaterial={handleDeleteAdditionalMaterial}
                  onEditAttendance={handleEditAttendance}
                />
              </div>
           </>
          ) : (
            <div className="w-full flex flex-col min-h-0">
              <AttendanceLessonsList
                lessons={currentLessons}
                selectedLesson={selectedLesson ? selectedLesson : undefined}
                onLessonSelect={setSelectedLesson}
                onCreateLesson={handleCreateLesson}
              />
            </div>
          )
        )}
        
        {/* Модалка создания урока */}
        <CreateLessonModal
          isOpen={isCreateLessonModalOpen}
          onClose={() => setIsCreateLessonModalOpen(false)}
          onSubmit={handleSubmitLesson}
        />
        
        {/* Модалка добавления курса */}
        <AddCourseModal
          isOpen={isAddCourseModalOpen}
          onClose={() => setIsAddCourseModalOpen(false)}
          onSubmit={handleAddCourse}
        />
      </div>
    </div>
  )
}
